/**
 * <NextSessionHero> — the very next live, in large (organism).
 *
 * The imminent session is the parent's first reason to open the app, so it gets the big signed
 * avatar and the day + time in the **largest type on the page** — le gros texte va à
 * l'information, pas au nom du bloc qui la contient. Dessous, la ligne de la séance
 * (`<SessionDetailsLine>`) : la pile de ses matières, puis « Maths · Physique-Chimie · avec
 * Wajih · 1h », sur deux lignes au plus. C'est l'anatomie de TOUTE séance de l'espace parent — les
 * lignes de « À venir » et de « Tous mes lives » (`<SessionRow>`) en sont la version compacte.
 * **Un seul badge d'état, et au plus un de contexte** (`useSessionBadges` → `<LiveStatusBadges>`),
 * sous la ligne de la séance, à toutes les largeurs, comme sur les lignes : rien n'est calé à
 * droite. Le jour même, c'est « Aujourd'hui » et son décompte — « dans 1 h 42 », « dans 42 min » —
 * puis « En cours » de son début à sa fin ; une séance suspendue ne porte que « Paiement en
 * attente ». Le contexte (« Retard », « Remplaçant », « Rattrapage », « Replanifié ») suit, discret.
 * La règle et son horloge sont dans `sessionBadges` : la carte ne décide d'aucun badge.
 *
 * **« Rejoindre », de 15 min avant le début à la fin** (`isJoinWindowOpen`) : un bouton principal
 * — pleine largeur sous les infos quand le bloc est étroit ; quand il est large, à droite, calé sur
 * le bas des infos, sans rien ajouter à la hauteur du bloc. C'est le
 * geste « Rejoindre en tant que … » de la feuille d'actions, posé sur la carte à l'heure où il
 * devient LE geste ; il apparaît et disparaît avec l'horloge des badges, qui restent affichés.
 * Hors de la fenêtre, le décompte tient seul la place. Pas de `onJoin`, pas de bouton : c'est ainsi
 * que l'appelant dit que la bascule sur l'espace de l'enfant n'est pas possible.
 *
 * **L'élève, lui, garde son bouton en permanence** (`joinDisplay="always"`, `voice="student"`) :
 * c'est le « GO ! » de son accueil (`NextLiveSessionBanner.js`), présent toute la journée du live
 * et les autres jours. Plein le jour du live, où il fait entrer ; secondaire les autres jours, et
 * cadenassé quand le paiement est en attente : il presse toujours, et c'est l'appelant qui dit
 * alors pourquoi on n'entre pas.
 *
 * **La mission de Sam a sa propre carte, sous celle-ci** : les jours où elle est là, la carte du
 * live ne parle que du live et ne porte son bouton que le jour même (`joinDisplay="today"`). Une
 * seule phrase et un seul bouton par carte, toujours.
 *
 * **Aucun menu « ⋮ »** : sur l'accueil, les quatre gestes couvrent ce que le menu offrait, et un
 * point de plus par séance faisait trois menus sur un écran qui n'en demandait aucun. À la place,
 * TOUT le bloc presse et ouvre les actions de cette séance — la même feuille que les lignes.
 *
 * **Il ne dessine pas sa propre carte** : sur l'accueil il est posé seul dans une `<CardList>`,
 * qu'encadre le dégradé des temps forts. Une story qui le montre seul fait de même.
 *
 * **L'heure est surlignée** (`<Highlight>`, V16 chap. 06) — elle seule, pas la date : c'est le
 * mot que le parent vient chercher. Si la ligne manque de place, l'heure passe dessous d'un bloc.
 *
 * Pas d'état estompé : l'accueil, son seul lecteur, écarte les séances où l'absence est signalée
 * et celles qui sont terminées (elles vivent, grisées, dans « Tous mes lives »).
 *
 * **L'espace masteur pose la même carte** : rien à la place de la capsule du tuteur (`lead={null}` :
 * ce serait la sienne) ; à la place de la ligne de la séance, la sienne (`details`) — le type de
 * cours et l'école, puis ses élèves un par un, leur classe et leurs matières ; et, sous
 * « Rejoindre », « Préparer » (`secondaryAction`), le chemin vers la préparation du live. Sans ces
 * props, la carte du parent et de l'élève, inchangée.
 *
 * La taille se lit sur la LARGEUR DU CONTENEUR (`onLayout`), pas sur la fenêtre : dans une colonne
 * de droite ou une modale, un bloc calibré sur l'écran déborde. Large, c'est l'avatar 72 et le
 * titre `text-3xl` ; en dessous, 54 et `text-2xl`, puis `text-xl` sur un téléphone.
 */
import React from "react"
import { Pressable, View, type LayoutChangeEvent, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Highlight } from "../../atoms/Highlight"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { useSessionBadges } from "../../hooks/useSessionBadges"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { LiveStatusBadges } from "../../molecules/LiveStatusBadges"
import { SessionDetailsLine } from "../../molecules/SessionDetailsLine"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { type FontSizeKey } from "../../theme/tokens/typography"
import { type Voice } from "../../utils/voice"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { formatDayLabel, formatHour, isToday } from "../../../ports/primary/ui/formatters/dateFormatter"
import {
    isJoinWindowOpen,
    isPaymentBlocked,
    tutorShortName,
} from "../../../ports/primary/ui/formatters/liveSessionFormatter"

/** Le mot du bouton d'entrée, par voix : celui du legacy de chaque espace. */
const JOIN_LABEL: Record<Voice, string> = {
    parent: "Rejoindre",
    student: "GO !",
}

/**
 * Quand le bouton d'entrée se montre : dans la fenêtre d'entrée (le parent), toujours (l'élève),
 * ou toute la journée du live seulement (l'élève, quand la mission de Sam porte l'action des autres
 * jours, sur sa propre carte).
 */
export type NextSessionJoinDisplay = "window" | "always" | "today"

export type NextSessionHeroProps = {
    session: UILiveSession
    /** Ouvre les actions de cette séance — tout le bloc est la cible. */
    onPress?: () => void
    /**
     * « Rejoindre » : fait entrer l'enfant dans cette séance. ABSENT = pas de bouton. Présent, il ne
     * se montre que dans la fenêtre d'entrée (`isJoinWindowOpen`), relue à chaque minute.
     */
    onJoin?: () => void
    /**
     * `"window"` (défaut) : le bouton ne se montre que dans la fenêtre d'entrée. `"always"` : il
     * reste là, plein le jour du live, secondaire les autres jours, cadenassé si le paiement est
     * en attente. `onJoin` part dans tous les cas : c'est l'appelant qui fait entrer ou qui explique.
     * `"today"` : comme `"always"` le jour du live (et dans la fenêtre d'entrée), aucun bouton les
     * autres jours.
     */
    joinDisplay?: NextSessionJoinDisplay
    /** À qui la carte parle : le mot du bouton (« Rejoindre », ou le « GO ! » de l'élève). */
    voice?: Voice
    /**
     * Ce qui se pose à gauche à la place de la capsule du tuteur. `null` : rien (le masteur) — et pas
     * de place gardée pour le nœud, qui n'est pas là.
     */
    lead?: React.ReactNode
    /**
     * Ce qui se pose sous la date à la place de la ligne de la séance (`<SessionDetailsLine>`) : celle
     * du masteur, qui nomme ses élèves. Absent : la ligne de toute séance.
     */
    details?: React.ReactNode
    /**
     * Un second geste, en lien sous « Rejoindre » (ou sous les infos quand le bouton n'est pas là) :
     * « Préparer », chez le masteur. Absent : rien.
     */
    secondaryAction?: { label: string; onPress: () => void }
    testID?: string
}

/**
 * Trois marches, mesurées sur SON conteneur : le bloc en grand (une colonne de tableau de bord),
 * le bloc intermédiaire, et le téléphone — où la date doit tenir sur UNE ligne, sans quoi
 * « Jeudi 17 septembre · 18h00 » se coupe en deux et le bloc perd ce qui en fait un bloc.
 */
const WIDE_FROM = 480
const MEDIUM_FROM = 380

export type NextSessionHeroLayout = {
    /** À poser sur la vue qui porte le bloc : c'est elle qu'on mesure. */
    onLayout: (event: LayoutChangeEvent) => void
    wide: boolean
    /** La date et l'heure : `text-3xl` en grand, `text-2xl` au milieu, `text-xl` sur un téléphone. */
    titleSize: FontSizeKey
    /** L'avatar signé du tuteur : 72 en grand, 54 en dessous. */
    avatarSize: number
    /** Le retrait du bloc, sur ses quatre côtés. */
    padding: SpacingKey
}

/**
 * La géométrie du bloc, lue sur la largeur de SON conteneur — exportée pour son squelette, qui
 * grandit donc aux mêmes marches. Étroit tant que rien n'est mesuré : un titre coupé au premier
 * rendu se verrait, un titre qui grandit ne se voit pas.
 */
export function useNextSessionHeroLayout(): NextSessionHeroLayout {
    const wide = useWidthAtLeast(WIDE_FROM)
    const medium = useWidthAtLeast(MEDIUM_FROM)
    return {
        onLayout: (event) => {
            wide.onLayout(event)
            medium.onLayout(event)
        },
        wide: wide.atLeast,
        titleSize: wide.atLeast ? "text-3xl" : medium.atLeast ? "text-2xl" : "text-xl",
        avatarSize: wide.atLeast ? 72 : 54,
        padding: wide.atLeast ? "6" : "4",
    }
}

export function NextSessionHero(props: NextSessionHeroProps) {
    const {
        session,
        onPress,
        onJoin,
        joinDisplay = "window",
        voice = "parent",
        lead,
        details,
        secondaryAction,
        testID,
    } = props
    const { date, tutor, recoveryInfo } = session

    const { onLayout: measure, wide, titleSize, avatarSize, padding } = useNextSessionHeroLayout()

    const tutorName = tutorShortName(tutor)

    // L'horloge des badges date aussi « Rejoindre » : le tic qui passe la fin de la séance l'arrête.
    const { badges, now } = useSessionBadges(session)
    const liveDay = isToday(date, now)
    const joinOpen =
        onJoin != null &&
        (joinDisplay === "always" || (joinDisplay === "today" && liveDay) || isJoinWindowOpen(session, now))
    // Hors de la fenêtre d'entrée, le bouton n'existe qu'en `"always"` et `"today"` : il n'est plein que
    // le jour du live, quand rien ne retient à la porte (la règle du « GO ! » de l'élève, toute la
    // journée).
    const blocked = isPaymentBlocked(session)
    const joinEnters = joinDisplay === "window" || (!blocked && liveDay)
    // Large, le bouton se pose à droite des infos.
    const joinBeside = wide && joinOpen

    const content = (
        <Row gap="4" align="center" style={lead === null ? undefined : styles.row}>
            {lead !== undefined ? lead : <Avatar uri={tutor.avatarUrl} name={tutorName} size={avatarSize} signed />}

            <Stack gap="2" style={styles.body}>
                <Row gap="1" align="center" style={styles.when}>
                    <Title size={titleSize} color="text-primary">{`${formatDayLabel(date, false)} ·`}</Title>
                    <Highlight size={titleSize}>{formatHour(date)}</Highlight>
                </Row>

                {details !== undefined ? details : <SessionDetailsLine session={session} />}

                {badges != null && (
                    <Row gap="2" align="center" style={styles.badges}>
                        <LiveStatusBadges badges={badges} />
                    </Row>
                )}

                {recoveryInfo != null && recoveryInfo !== "" && (
                    <Text size="text-xs" color="text-muted">
                        {recoveryInfo}
                    </Text>
                )}
            </Stack>
        </Row>
    )

    // Le bouton est posé HORS de la zone qui presse : un bouton dans un bouton, le lecteur d'écran
    // n'en annonce qu'un, et le toucher de l'un ne doit jamais ouvrir l'autre.
    const pressableInfo = (style: StyleProp<ViewStyle>) =>
        onPress == null ? (
            <View style={style}>{content}</View>
        ) : (
            <Pressable style={style} onPress={onPress} accessibilityRole="button">
                {content}
            </Pressable>
        )
    const join = (
        <Button
            label={JOIN_LABEL[voice]}
            variant={joinEnters ? "solid" : "outline"}
            iconName={joinDisplay !== "window" && blocked ? "lock" : undefined}
            onPress={onJoin}
            fullWidth={!wide}
        />
    )

    // Le second geste (« Préparer ») se pose sous « Rejoindre » ; sans lui, le bouton reste seul, sans enveloppe.
    const actions =
        secondaryAction == null ? (
            join
        ) : (
            <Stack gap="2" align={joinBeside ? "end" : joinOpen ? "center" : "start"}>
                {joinOpen && join}
                <TextButton label={secondaryAction.label} onPress={secondaryAction.onPress} size="sm" />
            </Stack>
        )

    // Large : le bouton à droite des infos, bas contre bas.
    if (joinBeside)
        return (
            <View testID={testID} onLayout={measure} style={[styles.root(padding), styles.beside]}>
                {pressableInfo(styles.body)}
                {actions}
            </View>
        )

    const below = joinOpen || secondaryAction != null

    return (
        <View testID={testID} onLayout={measure}>
            {pressableInfo([styles.root(padding), below ? styles.aboveJoin : undefined])}
            {below && <View style={styles.joinBelow}>{actions}</View>}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: (padding: SpacingKey) => ({
        paddingHorizontal: theme.spacing[padding],
        paddingVertical: theme.spacing[padding],
    }),
    /** Avec le bouton, le bas du bloc est le sien : les infos lui laissent leur marge basse. */
    aboveJoin: {
        paddingBottom: 0,
    },
    /** « Rejoindre » sur un bloc étroit : sous les infos, pleine largeur. */
    joinBelow: {
        paddingHorizontal: theme.spacing["4"],
        paddingTop: theme.spacing["3"],
        paddingBottom: theme.spacing["4"],
    },
    /** Bloc large avec « Rejoindre » : les infos à gauche, le bouton à droite, bas contre bas. */
    beside: {
        flexDirection: "row",
        alignItems: "flex-end",
        gap: theme.spacing["4"],
    },
    /** Room for the tutor bow-tie that hangs below the avatar. */
    row: {
        paddingBottom: 6,
    },
    body: {
        flex: 1,
        flexShrink: 1,
    },
    when: {
        flexWrap: "wrap",
    },
    badges: {
        flexWrap: "wrap",
        flexShrink: 0,
    },
}))
